import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchThemes, createTheme, fetchThemePeople, updateThemePeople, fetchTasks, deleteTheme, createTask, deleteTask, updateTheme, updateTask } from '../api/projects';
import { fetchPeople } from '../api/people';
import type { Theme, Task, TaskStatus, TaskPriority } from '../types/project';
import type { Person } from '../types/people';
import AvatarStack from '../components/AvatarStack';
import { TASK_STATUSES, STATUS_LABEL, STATUS_BADGE } from '../utils/taskStatus';

type StatusFilter = TaskStatus | 'ALL';
type PriorityFilter = TaskPriority | 'ALL';

interface TaskFilter {
  status: StatusFilter;
  priority: PriorityFilter;
}

function getWeekBounds(): { start: Date; end: Date } {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = new Date(now);
  start.setDate(now.getDate() + mondayOffset);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function isDueThisWeek(dueDate: string | null): boolean {
  if (!dueDate?.trim()) return false;
  const { start, end } = getWeekBounds();
  const d = new Date(dueDate.trim());
  d.setHours(12, 0, 0, 0);
  return d >= start && d <= end;
}

function filterTasks(
  tasks: Task[],
  status: StatusFilter,
  priority: PriorityFilter
): { total: number; dueThisWeek: number } {
  let filtered = tasks;
  if (status !== 'ALL') filtered = filtered.filter((t) => t.status === status);
  if (priority !== 'ALL') filtered = filtered.filter((t) => (t.priority ?? 'MEDIUM') === priority);
  const total = filtered.length;
  const dueThisWeek = filtered.filter((t) => isDueThisWeek(t.due_date)).length;
  return { total, dueThisWeek };
}

const DEFAULT_TASK_FILTER: TaskFilter = { status: 'ALL', priority: 'ALL' };

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  ...TASK_STATUSES.map((s) => ({ value: s as StatusFilter, label: STATUS_LABEL[s] })),
];
const PRIORITY_OPTIONS: { value: PriorityFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

const COL_GAP = 24;
const COL = {
  expand: 32,
  status: 100,
  priority: 100,
  tasks: 56,
  due: 110,
  people: 112,
  delete: 32,
} as const;

interface ThemeNodeProps {
  theme: Theme;
  depth: number;
  childrenThemes: Theme[];
  isRoot: boolean;
  people: Person[];
  assignedIds: string[];
  themeAssignees: Record<string, string[]>;
  themeTasks: Record<string, Task[]>;
  taskFiltersByThemeId: Record<string, TaskFilter>;
  getTaskStats: (themeId: string, isRoot: boolean, childThemeIds: string[]) => { total: number; dueThisWeek: number };
  onAssignmentsChange: (themeId: string, personIds: string[]) => void;
  onTaskFilterChange: (themeId: string, filter: TaskFilter, propagateToChildren: boolean) => void;
  onEditTheme: (theme: Theme) => void;
  onEditTask: (task: Task) => void;
  onDeleteSubTheme: (theme: Theme) => void;
  onDeleteTask: (task: Task) => void;
  onTaskStatusChange: (taskId: string, themeId: string, status: TaskStatus) => void;
  onTaskPriorityChange: (taskId: string, themeId: string, priority: TaskPriority) => void;
}

function ThemeNode({
  theme,
  depth,
  childrenThemes,
  isRoot,
  people,
  assignedIds,
  themeAssignees,
  themeTasks,
  taskFiltersByThemeId,
  getTaskStats,
  onAssignmentsChange,
  onTaskFilterChange,
  onEditTheme,
  onEditTask,
  onDeleteSubTheme,
  onDeleteTask,
  onTaskStatusChange,
  onTaskPriorityChange,
}: ThemeNodeProps) {
  const tasksForTheme = themeTasks[theme.id] ?? [];
  const isExpandable = (isRoot && childrenThemes.length > 0) || (!isRoot && tasksForTheme.length > 0);
  // Default collapsed on page entry; users can expand manually.
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState<null | { kind: 'theme' | 'task'; id: string }>(null);
  const [pendingIds, setPendingIds] = useState<string[]>(assignedIds);
  const pickerRef = useRef<HTMLDivElement>(null);

  const taskFilter = taskFiltersByThemeId[theme.id] ?? DEFAULT_TASK_FILTER;
  const taskStats = getTaskStats(theme.id, isRoot, childrenThemes.map((c) => c.id));
  const rowBg = isRoot
    ? (open && isExpandable ? 'var(--color-theme-row-open)' : 'var(--color-theme-row-closed)')
    : 'var(--color-subtheme-row)';

  const handleSaveAssignments = useCallback(() => {
    updateThemePeople(theme.id, pendingIds).then(() => {
      onAssignmentsChange(theme.id, pendingIds);
      setPickerOpen(false);
    });
  }, [theme.id, pendingIds, onAssignmentsChange]);

  const togglePerson = useCallback((personId: string) => {
    setPendingIds((prev) =>
      prev.includes(personId) ? prev.filter((id) => id !== personId) : [...prev, personId]
    );
  }, []);

  useEffect(() => {
    setPendingIds(assignedIds);
  }, [assignedIds]);

  useEffect(() => {
    if (!isExpandable) setOpen(false);
  }, [isExpandable]);

  return (
    <div style={{ marginLeft: depth ? `${depth * 16}px` : 0, marginBottom: '4px' }}>
      <div
        className="theme-tree-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: COL_GAP,
          padding: '10px 12px',
          borderRadius: 'var(--radius)',
          background: rowBg,
          transition: 'background 150ms ease',
        }}
      >
        <div style={{ width: COL.expand, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isExpandable ? (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px' }}
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              {open ? '▼' : '▶'}
            </button>
          ) : null}
        </div>
        <Link
          to={`/projects/theme/${theme.id}`}
          style={{
            flex: 1,
            color: 'var(--color-text-primary)',
            textDecoration: 'none',
            fontWeight: 500,
            paddingLeft: isRoot ? 0 : '8px',
            minWidth: 0,
          }}
        >
          {theme.name}
        </Link>
        <div style={{ width: COL.status, flexShrink: 0, display: 'flex', justifyContent: 'center' }} onClick={(e) => e.preventDefault()}>
          <select
            value={taskFilter.status}
            onChange={(e) => {
              const status = e.target.value as StatusFilter;
              onTaskFilterChange(theme.id, { ...taskFilter, status }, isRoot);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              fontSize: '12px',
              padding: '4px 6px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--color-border)',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div style={{ width: COL.priority, flexShrink: 0, display: 'flex', justifyContent: 'center' }} onClick={(e) => e.preventDefault()}>
          <select
            value={taskFilter.priority}
            onChange={(e) => {
              const priority = e.target.value as PriorityFilter;
              onTaskFilterChange(theme.id, { ...taskFilter, priority }, isRoot);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              fontSize: '12px',
              padding: '4px 6px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--color-border)',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div
          style={{
            width: COL.tasks,
            flexShrink: 0,
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {taskStats.total}
        </div>
        <div
          style={{
            width: COL.due,
            flexShrink: 0,
            fontSize: '13px',
            color: 'var(--color-blue-dark)',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {taskStats.dueThisWeek}
        </div>
        <div
          ref={pickerRef}
          style={{ position: 'relative', width: COL.people, flexShrink: 0, display: 'flex', justifyContent: 'center' }}
          onClick={(e) => e.preventDefault()}
        >
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setPickerOpen((o) => !o);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            <AvatarStack people={people} personIds={assignedIds} size={20} />
            <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>▼</span>
          </button>
          {pickerOpen && (
            <>
              <div
                role="presentation"
                style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                onClick={() => setPickerOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '4px',
                  zIndex: 11,
                  minWidth: '200px',
                  padding: '12px',
                  background: 'white',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
                  Assign people
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
                  {people.length === 0 ? (
                    <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>No people</span>
                  ) : (
                    people.map((person) => {
                      const id = person.id;
                      const isChecked = pendingIds.includes(id);
                      return (
                        <div
                          key={id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            togglePerson(id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              togglePerson(id);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderRadius: 'var(--radius)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            readOnly
                            tabIndex={-1}
                            style={{ pointerEvents: 'none', flexShrink: 0 }}
                          />
                          <span>{person.name}</span>
                        </div>
                      );
                    })
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSaveAssignments}
                  className="btn btn-primary btn-sm"
                  style={{ width: '100%' }}
                >
                  Save
                </button>
              </div>
            </>
          )}
        </div>
        <div
          style={{ width: COL.delete, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', position: 'relative' }}
          onClick={(e) => e.preventDefault()}
        >
          <button
            type="button"
            aria-label={`Actions for ${isRoot ? 'theme' : 'sub-theme'} ${theme.name}`}
            title="Actions"
            className="subtheme-delete"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((cur) => (cur?.kind === 'theme' && cur.id === theme.id ? null : { kind: 'theme', id: theme.id }));
            }}
            style={{
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-muted)',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ⋮
          </button>
          {menuOpen?.kind === 'theme' && menuOpen.id === theme.id && (
            <>
              <div
                role="presentation"
                style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                onClick={() => setMenuOpen(null)}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: '6px',
                  zIndex: 31,
                  minWidth: '160px',
                  background: 'white',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.14)',
                  overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(null);
                    onEditTheme(theme);
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Edit
                </button>
                {!isRoot && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(null);
                      onDeleteSubTheme(theme);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: 'var(--color-coral)',
                      borderTop: '1px solid var(--color-border)',
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {open && isRoot && childrenThemes.length > 0 && (
        <div>
          {childrenThemes.map((child) => (
            <ThemeNode
              key={child.id}
              theme={child}
              depth={depth + 1}
              childrenThemes={[]}
              isRoot={false}
              people={people}
              assignedIds={themeAssignees[child.id] ?? []}
              themeAssignees={themeAssignees}
              themeTasks={themeTasks}
              taskFiltersByThemeId={taskFiltersByThemeId}
              getTaskStats={getTaskStats}
              onAssignmentsChange={onAssignmentsChange}
              onTaskFilterChange={onTaskFilterChange}
              onEditTheme={onEditTheme}
              onEditTask={onEditTask}
              onDeleteSubTheme={onDeleteSubTheme}
              onDeleteTask={onDeleteTask}
              onTaskStatusChange={onTaskStatusChange}
              onTaskPriorityChange={onTaskPriorityChange}
            />
          ))}
        </div>
      )}
      {open && !isRoot && tasksForTheme.length > 0 && (
        <div>
          {tasksForTheme
            .filter((t) => {
              if (taskFilter.status !== 'ALL' && t.status !== taskFilter.status) return false;
              if (taskFilter.priority !== 'ALL' && (t.priority ?? 'MEDIUM') !== taskFilter.priority) return false;
              return true;
            })
            .map((t) => {
            const dueYesNo = isDueThisWeek(t.due_date) ? 'Yes' : 'No';
            return (
              <div key={t.id} style={{ marginLeft: `${(depth + 1) * 16}px`, marginBottom: '4px' }}>
                <div
                  className="theme-tree-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: COL_GAP,
                    padding: '10px 12px',
                    borderRadius: 'var(--radius)',
                    background: 'rgba(255, 255, 255, 0.78)',
                  }}
                >
                  <div style={{ width: COL.expand, flexShrink: 0 }} />
                  <Link
                    to={`/projects/task/${t.id}`}
                    style={{
                      flex: 1,
                      color: 'var(--color-text-primary)',
                      textDecoration: 'none',
                      fontWeight: 500,
                      minWidth: 0,
                      paddingLeft: '8px',
                    }}
                  >
                    {t.title}
                  </Link>
                  <div style={{ width: COL.status, flexShrink: 0, display: 'flex', justifyContent: 'center' }} onClick={(e) => e.preventDefault()}>
                    <select
                      value={t.status}
                      onChange={(e) => onTaskStatusChange(t.id, t.theme_id, e.target.value as TaskStatus)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        fontSize: '12px',
                        padding: '4px 6px',
                        borderRadius: 'var(--radius)',
                        border: 'none',
                        background: STATUS_BADGE[t.status].bg,
                        color: STATUS_BADGE[t.status].color,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      {TASK_STATUSES.map((s) => (
                        <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: COL.priority, flexShrink: 0, display: 'flex', justifyContent: 'center' }} onClick={(e) => e.preventDefault()}>
                    <select
                      value={t.priority ?? 'MEDIUM'}
                      onChange={(e) => onTaskPriorityChange(t.id, t.theme_id, e.target.value as TaskPriority)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        fontSize: '12px',
                        padding: '4px 6px',
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--color-border)',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                    </select>
                  </div>
                  <div style={{ width: COL.tasks, flexShrink: 0 }} />
                  <div style={{ width: COL.due, flexShrink: 0, textAlign: 'center', fontSize: '13px', color: 'var(--color-blue-dark)' }}>
                    {dueYesNo}
                  </div>
                  <div style={{ width: COL.people, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <AvatarStack people={people} personIds={t.assignee_ids ?? []} size={20} />
                  </div>
                  <div
                    style={{ width: COL.delete, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', position: 'relative' }}
                    onClick={(e) => e.preventDefault()}
                  >
                    <button
                      type="button"
                      aria-label={`Actions for task ${t.title}`}
                      title="Actions"
                      className="subtheme-delete"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen((cur) => (cur?.kind === 'task' && cur.id === t.id ? null : { kind: 'task', id: t.id }));
                      }}
                      style={{
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-text-muted)',
                        fontSize: '18px',
                        lineHeight: 1,
                      }}
                    >
                      ⋮
                    </button>
                    {menuOpen?.kind === 'task' && menuOpen.id === t.id && (
                      <>
                        <div
                          role="presentation"
                          style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                          onClick={() => setMenuOpen(null)}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            marginTop: '6px',
                            zIndex: 31,
                            minWidth: '160px',
                            background: 'white',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius)',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.14)',
                            overflow: 'hidden',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(null);
                              onEditTask(t);
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '10px 12px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '13px',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(null);
                              onDeleteTask(t);
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '10px 12px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: 'var(--color-coral)',
                              borderTop: '1px solid var(--color-border)',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [themeAssignees, setThemeAssignees] = useState<Record<string, string[]>>({});
  const [themeTasks, setThemeTasks] = useState<Record<string, Task[]>>({});
  const [taskFiltersByThemeId, setTaskFiltersByThemeId] = useState<Record<string, TaskFilter>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editKind, setEditKind] = useState<'theme' | 'task'>('theme');
  const [editingThemeId, setEditingThemeId] = useState<string>('');
  const [editingTaskId, setEditingTaskId] = useState<string>('');
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editParentThemeId, setEditParentThemeId] = useState<string>(''); // for themes: '' => root
  const [editTaskStatus, setEditTaskStatus] = useState<TaskStatus>('TO_DO');
  const [editTaskPriority, setEditTaskPriority] = useState<TaskPriority>('MEDIUM');
  const [editTaskDueDate, setEditTaskDueDate] = useState('');
  const [editTaskAssignees, setEditTaskAssignees] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newParentId, setNewParentId] = useState<string>('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [createTaskError, setCreateTaskError] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskThemeId, setNewTaskThemeId] = useState('');
  const [newTaskSubThemeId, setNewTaskSubThemeId] = useState('');
  const [newTaskStatus, setNewTaskStatus] = useState<TaskStatus>('TO_DO');
  const [newTaskPriority, setNewTaskPriority] = useState<TaskPriority>('MEDIUM');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');

  const loadThemes = useCallback((showLoading = false) => {
    if (showLoading) setLoading(true);
    fetchThemes()
      .then((themeList) => {
        setThemes(themeList);
        return Promise.all([
          fetchPeople(),
          ...themeList.map((t) =>
            fetchThemePeople(t.id).then((peopleList) => ({ themeId: t.id, ids: peopleList.map((p) => p.id) }))
          ),
          ...themeList.map((t) =>
            fetchTasks(t.id).then((tasks) => ({ themeId: t.id, tasks }))
          ),
        ]);
      })
      .then((results) => {
        const n = (results.length - 1) / 2;
        const peopleList = results[0] as Person[];
        const assignmentResults = results.slice(1, 1 + n) as { themeId: string; ids: string[] }[];
        const taskResults = results.slice(1 + n) as { themeId: string; tasks: Task[] }[];
        setPeople(peopleList);
        const assignMap: Record<string, string[]> = {};
        assignmentResults.forEach(({ themeId, ids }) => { assignMap[themeId] = ids; });
        setThemeAssignees(assignMap);
        const taskMap: Record<string, Task[]> = {};
        taskResults.forEach(({ themeId, tasks }) => { taskMap[themeId] = tasks; });
        setThemeTasks(taskMap);
      })
      .catch((e) => setError(e.message))
      .finally(() => {
        if (showLoading) setLoading(false);
      });
  }, []);

  const handleAssignmentsChange = useCallback((themeId: string, personIds: string[]) => {
    setThemeAssignees((prev) => ({ ...prev, [themeId]: personIds }));
  }, []);

  const byParent = useMemo(() => themes.reduce<Record<string, Theme[]>>((acc, t) => {
    const pid = t.parent_theme_id ?? 'root';
    if (!acc[pid]) acc[pid] = [];
    acc[pid].push(t);
    return acc;
  }, {}), [themes]);

  const themesById = useMemo(() => {
    const m = new Map<string, Theme>();
    themes.forEach((t) => m.set(t.id, t));
    return m;
  }, [themes]);

  const getRootThemeId = useCallback((themeId: string): string => {
    let cur = themesById.get(themeId);
    // Walk up parent chain; return the top-most ancestor.
    while (cur?.parent_theme_id) {
      const parent = themesById.get(cur.parent_theme_id);
      if (!parent) break;
      cur = parent;
    }
    return cur?.id ?? themeId;
  }, [themesById]);

  const getTaskStats = useCallback(
    (themeId: string, isRoot: boolean, childThemeIds: string[]) => {
      let tasks: Task[];
      if (isRoot) {
        tasks = (childThemeIds.flatMap((cid) => themeTasks[cid] ?? []) as Task[]);
      } else {
        tasks = themeTasks[themeId] ?? [];
      }
      const filter = taskFiltersByThemeId[themeId] ?? DEFAULT_TASK_FILTER;
      return filterTasks(tasks, filter.status, filter.priority);
    },
    [themeTasks, taskFiltersByThemeId]
  );

  const handleTaskFilterChange = useCallback(
    (themeId: string, filter: TaskFilter, propagateToChildren: boolean) => {
      setTaskFiltersByThemeId((prev) => {
        const next = { ...prev, [themeId]: filter };
        if (propagateToChildren) {
          const childThemes = byParent[themeId] ?? [];
          childThemes.forEach((child) => {
            next[child.id] = filter;
          });
        }
        return next;
      });
    },
    [byParent]
  );

  const rootThemes = useMemo(() => themes.filter((t) => !t.parent_theme_id), [themes]);
  const subThemes = useMemo(() => themes.filter((t) => !!t.parent_theme_id), [themes]);
  const filteredSubThemes = useMemo(() => {
    if (!newTaskThemeId) return [];
    // Show all non-root themes under the selected root theme (even if nested).
    return subThemes.filter((t) => getRootThemeId(t.id) === newTaskThemeId);
  }, [newTaskThemeId, subThemes, getRootThemeId]);

  const handleTaskThemeChange = useCallback((themeId: string) => {
    setNewTaskThemeId(themeId);
    if (!themeId) {
      setNewTaskSubThemeId('');
      return;
    }
    const allowed = subThemes.filter((t) => getRootThemeId(t.id) === themeId);
    setNewTaskSubThemeId(allowed[0]?.id ?? '');
  }, [subThemes, getRootThemeId]);

  const handleTaskSubThemeChange = useCallback((subThemeId: string) => {
    setNewTaskSubThemeId(subThemeId);
    if (!subThemeId) return;
    const rootId = getRootThemeId(subThemeId);
    setNewTaskThemeId(rootId);
  }, [getRootThemeId]);

  const handleOpenAddTask = useCallback(() => {
    setCreateTaskError('');
    setNewTaskTitle('');
    const initialSubThemeId = subThemes[0]?.id ?? '';
    const initialThemeId = initialSubThemeId ? getRootThemeId(initialSubThemeId) : '';
    setNewTaskThemeId(initialThemeId);
    setNewTaskSubThemeId(initialSubThemeId);
    setNewTaskStatus('TO_DO');
    setNewTaskPriority('MEDIUM');
    setNewTaskDueDate('');
    setAddTaskOpen(true);
  }, [subThemes, getRootThemeId]);

  const handleCreateNewTask = useCallback(async () => {
    if (!newTaskTitle.trim() || !newTaskSubThemeId) return;
    setCreatingTask(true);
    setCreateTaskError('');
    try {
      await createTask({
        theme_id: newTaskSubThemeId,
        title: newTaskTitle.trim(),
        status: newTaskStatus,
        priority: newTaskPriority,
        due_date: newTaskDueDate || undefined,
      });
      setAddTaskOpen(false);
      setNewTaskTitle('');
      setNewTaskDueDate('');
      loadThemes(false);
    } catch (e) {
      setCreateTaskError(e instanceof Error ? e.message : 'Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  }, [newTaskTitle, newTaskSubThemeId, newTaskStatus, newTaskPriority, newTaskDueDate, loadThemes]);

  useEffect(() => {
    loadThemes(true);
  }, [loadThemes]);

  const handleCreateTheme = useCallback(async () => {
    if (!newName.trim()) {
      setCreateError('Name is required');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      await createTheme({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        parent_theme_id: newParentId || undefined,
      });
      setAddOpen(false);
      setNewName('');
      setNewDescription('');
      setNewParentId('');
      loadThemes(false);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create theme');
    } finally {
      setCreating(false);
    }
  }, [newName, newDescription, newParentId, loadThemes]);

  const handleDeleteSubTheme = useCallback(
    async (t: Theme) => {
      if (!window.confirm(`Delete sub-theme “${t.name}”?`)) return;
      try {
        await deleteTheme(t.id);
        loadThemes(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete sub-theme');
      }
    },
    [loadThemes]
  );

  const handleDeleteTask = useCallback(
    async (t: Task) => {
      if (!window.confirm(`Delete task “${t.title}”?`)) return;
      try {
        await deleteTask(t.id);
        loadThemes(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete task');
      }
    },
    [loadThemes]
  );

  const handleTaskStatusChange = useCallback(
    async (taskId: string, themeId: string, status: TaskStatus) => {
      setThemeTasks((prev) => ({
        ...prev,
        [themeId]: (prev[themeId] ?? []).map((t) => (t.id === taskId ? { ...t, status } : t)),
      }));
      try {
        await updateTask(taskId, { status });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update status');
        loadThemes(false);
      }
    },
    [loadThemes]
  );

  const handleTaskPriorityChange = useCallback(
    async (taskId: string, themeId: string, priority: TaskPriority) => {
      setThemeTasks((prev) => ({
        ...prev,
        [themeId]: (prev[themeId] ?? []).map((t) => (t.id === taskId ? { ...t, priority } : t)),
      }));
      try {
        await updateTask(taskId, { priority });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update priority');
        loadThemes(false);
      }
    },
    [loadThemes]
  );

  const handleEditTheme = useCallback(
    (t: Theme) => {
      setEditError('');
      setEditKind('theme');
      setEditingThemeId(t.id);
      setEditingTaskId('');
      setEditName(t.name ?? '');
      setEditDescription(t.description ?? '');
      setEditParentThemeId(t.parent_theme_id ?? '');
      setEditOpen(true);
    },
    []
  );

  const handleEditTask = useCallback(
    (t: Task) => {
      setEditError('');
      setEditKind('task');
      setEditingTaskId(t.id);
      setEditingThemeId('');
      setEditName(t.title ?? '');
      setEditDescription(t.description ?? '');
      setEditTaskStatus(t.status);
      setEditTaskPriority(t.priority);
      setEditTaskDueDate(t.due_date ? t.due_date.slice(0, 10) : '');
      setEditTaskAssignees(t.assignee_ids ?? (t.assignee ? [t.assignee] : []));
      setEditOpen(true);
    },
    []
  );

  const toggleEditAssignee = useCallback((personId: string) => {
    setEditTaskAssignees((prev) => (prev.includes(personId) ? prev.filter((id) => id !== personId) : [...prev, personId]));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    setSavingEdit(true);
    setEditError('');
    try {
      if (editKind === 'theme') {
        if (!editingThemeId || !editName.trim()) {
          setEditError('Name is required');
          return;
        }
        await updateTheme(editingThemeId, {
          name: editName.trim(),
          description: editDescription.trim(),
          parent_theme_id: editParentThemeId ? editParentThemeId : null,
        });
      } else {
        if (!editingTaskId || !editName.trim()) {
          setEditError('Title is required');
          return;
        }
        await updateTask(editingTaskId, {
          title: editName.trim(),
          description: editDescription.trim() || null,
          status: editTaskStatus,
          priority: editTaskPriority,
          due_date: editTaskDueDate ? editTaskDueDate : null,
          assignee_ids: editTaskAssignees,
        });
      }
      setEditOpen(false);
      loadThemes(false);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  }, [
    editKind,
    editingThemeId,
    editingTaskId,
    editName,
    editDescription,
    editParentThemeId,
    editTaskStatus,
    editTaskPriority,
    editTaskDueDate,
    editTaskAssignees,
    loadThemes,
  ]);

  if (loading) return <div>Loading themes…</div>;
  if (error) return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;

  function renderTree(theme: Theme, depth: number) {
    const children = byParent[theme.id] ?? [];
    return (
      <ThemeNode
        key={theme.id}
        theme={theme}
        depth={depth}
        childrenThemes={children}
        isRoot={!theme.parent_theme_id}
        people={people}
        assignedIds={themeAssignees[theme.id] ?? []}
        themeAssignees={themeAssignees}
        themeTasks={themeTasks}
        taskFiltersByThemeId={taskFiltersByThemeId}
        getTaskStats={getTaskStats}
        onAssignmentsChange={handleAssignmentsChange}
        onTaskFilterChange={handleTaskFilterChange}
        onEditTheme={handleEditTheme}
        onEditTask={handleEditTask}
        onDeleteSubTheme={handleDeleteSubTheme}
        onDeleteTask={handleDeleteTask}
        onTaskStatusChange={handleTaskStatusChange}
        onTaskPriorityChange={handleTaskPriorityChange}
      />
    );
  }

  return (
    <>
      <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-blue-dark)', fontWeight: 600, marginBottom: '8px' }}>
        Projects
      </p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: 700, margin: 0 }}>Theme tree</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button type="button" onClick={() => setAddOpen(true)} className="btn btn-primary">
            Add theme
          </button>
          <button type="button" onClick={handleOpenAddTask} className="btn btn-secondary">
            Add task
          </button>
        </div>
      </div>
      {rootThemes.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>No themes yet. Click “Add theme” to create your first theme.</p>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: COL_GAP,
              padding: '10px 12px',
              borderBottom: '1px solid var(--color-border)',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <div style={{ width: COL.expand, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>Theme</div>
            <div style={{ width: COL.status, flexShrink: 0, textAlign: 'center' }}>Status</div>
            <div style={{ width: COL.priority, flexShrink: 0, textAlign: 'center' }}>Priority</div>
            <div style={{ width: COL.tasks, flexShrink: 0, textAlign: 'center' }}>Tasks</div>
            <div style={{ width: COL.due, flexShrink: 0, textAlign: 'center' }}>Due this week</div>
            <div style={{ width: COL.people, flexShrink: 0, textAlign: 'center' }}>People</div>
            <div style={{ width: COL.delete, flexShrink: 0 }} />
          </div>
          {rootThemes.map((t) => renderTree(t, 0))}
        </div>
      )}

      {addOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 100,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setAddOpen(false)}
          role="presentation"
        >
          <div
            style={{
              width: '520px',
              background: 'white',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
              padding: '24px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px' }}>New theme</h2>
            {createError && (
              <p style={{ color: 'var(--color-coral)', marginBottom: '16px', fontSize: '14px' }}>{createError}</p>
            )}
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
              placeholder="Theme name"
            />
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Description (optional)</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px', resize: 'vertical' }}
              placeholder="Brief description"
            />
            {themes.length > 0 && (
              <>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Parent theme (optional)</label>
                <select
                  value={newParentId}
                  onChange={(e) => setNewParentId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '24px' }}
                >
                  <option value="">None (root theme)</option>
                  {themes.filter((t) => !t.parent_theme_id).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </>
            )}
            {themes.length === 0 && <div style={{ marginBottom: '24px' }} />}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleCreateTheme}
                disabled={creating || !newName.trim()}
                className="btn btn-primary"
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setAddOpen(false); setCreateError(''); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {addTaskOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 100,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setAddTaskOpen(false)}
          role="presentation"
        >
          <div
            style={{
              width: '520px',
              background: 'white',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
              padding: '24px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px' }}>New task</h2>
            {createTaskError && (
              <p style={{ color: 'var(--color-coral)', marginBottom: '16px', fontSize: '14px' }}>{createTaskError}</p>
            )}
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Title</label>
            <input
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
              placeholder="Task title"
            />

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Theme</label>
            <select
              value={newTaskThemeId}
              onChange={(e) => handleTaskThemeChange(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            >
              {rootThemes.length === 0 ? (
                <option value="">No themes yet</option>
              ) : (
                rootThemes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))
              )}
            </select>

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Sub-theme</label>
            <select
              value={newTaskSubThemeId}
              onChange={(e) => handleTaskSubThemeChange(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            >
              {!newTaskThemeId ? (
                <option value="">Select a theme first</option>
              ) : filteredSubThemes.length === 0 ? (
                <option value="">No sub-themes for this theme</option>
              ) : (
                filteredSubThemes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))
              )}
            </select>

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Status</label>
            <select
              value={newTaskStatus}
              onChange={(e) => setNewTaskStatus(e.target.value as TaskStatus)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Priority</label>
            <select
              value={newTaskPriority}
              onChange={(e) => setNewTaskPriority(e.target.value as TaskPriority)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Due date</label>
            <input
              type="date"
              value={newTaskDueDate}
              onChange={(e) => setNewTaskDueDate(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '24px' }}
            />

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleCreateNewTask}
                disabled={creatingTask || !newTaskTitle.trim() || !newTaskSubThemeId || filteredSubThemes.length === 0}
                className="btn btn-primary"
              >
                {creatingTask ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setAddTaskOpen(false); setCreateTaskError(''); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 110,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setEditOpen(false)}
          role="presentation"
        >
          <div
            style={{
              width: '560px',
              background: 'white',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
              padding: '24px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px' }}>{editKind === 'theme' ? 'Edit theme' : 'Edit task'}</h2>
            {editError && (
              <p style={{ color: 'var(--color-coral)', marginBottom: '16px', fontSize: '14px' }}>{editError}</p>
            )}

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>
              {editKind === 'theme' ? 'Name' : 'Title'}
            </label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
              placeholder={editKind === 'theme' ? 'Theme name' : 'Task title'}
            />

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Description (optional)</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px', resize: 'vertical' }}
              placeholder="Brief description"
            />

            {editKind === 'theme' ? (
              <>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Parent theme</label>
                <select
                  value={editParentThemeId}
                  onChange={(e) => setEditParentThemeId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                >
                  <option value="">None (root theme)</option>
                  {rootThemes
                    .filter((t) => t.id !== editingThemeId)
                    .map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
              </>
            ) : (
              <>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Status</label>
                <select
                  value={editTaskStatus}
                  onChange={(e) => setEditTaskStatus(e.target.value as TaskStatus)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>

                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Priority</label>
                <select
                  value={editTaskPriority}
                  onChange={(e) => setEditTaskPriority(e.target.value as TaskPriority)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>

                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Due date</label>
                <input
                  type="date"
                  value={editTaskDueDate}
                  onChange={(e) => setEditTaskDueDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                />

                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
                  Assignees
                </div>
                <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '10px', marginBottom: '16px' }}>
                  {people.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>No people</div>
                  ) : (
                    people.map((p) => {
                      const checked = editTaskAssignees.includes(p.id);
                      return (
                        <div
                          key={p.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleEditAssignee(p.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') toggleEditAssignee(p.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderRadius: 'var(--radius)',
                          }}
                        >
                          <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
                          <span>{p.name}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit || !editName.trim()}
                className="btn btn-primary"
              >
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setEditOpen(false); setEditError(''); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
